'use client';

import { ReactNode, createContext, useCallback, useContext, useReducer } from 'react';

export type State = {
  categoryId: number;
  subcategoryId?: number;
};

const initialState = { categoryId: -1 };

type Action = {
  type: 'SELECT';
  payload: {
    categoryId: number;
    subcategoryId?: number;
  };
};

const categoryReducer = (state: State, action: Action) => {
  switch (action.type) {
    case 'SELECT':
      if (action.payload.subcategoryId) {
        const isSelected =
          state.categoryId === action.payload.categoryId &&
          state.subcategoryId === action.payload.subcategoryId;
        return isSelected ? { categoryId: state.categoryId } : action.payload;
      }
      return state.categoryId === action.payload.categoryId ? { categoryId: -1 } : action.payload;
    default:
      return state;
  }
};

type CategoryContextType =
  | {
    state: State;
    select: (item: State) => void;
  }
  | undefined;

const CategoryContext = createContext<CategoryContextType>(undefined);

export const CategoryProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [state, dispatch] = useReducer(categoryReducer, initialState);

  const select = useCallback((item: State) => {
    dispatch({ type: 'SELECT', payload: item });
  }, []);

  return <CategoryContext.Provider value={{ state, select }}>{children}</CategoryContext.Provider>;
};

export const useCategory = () => {
  const context = useContext(CategoryContext);
  if (!context) {
    throw new Error('useCategory must be used within a CategoryProvider');
  }
  return context;
};
